import { useLocation, Link } from "react-router-dom";
import { useState } from "react";
import { ExternalLink, Truck, ChevronDown, ChevronUp } from "lucide-react";
import BuyerOrderTracker, { StatusPill, useLiveOrders } from "@/components/OrderTracking";

function inrDate(iso) {
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function OrderCard({ order: o }) {
  const [expanded, setExpanded] = useState(false);
  const firstItem = (o.items || [])[0];
  const extraCount = Math.max(0, (o.items || []).length - 1);
  const soldBy = (o.vendor_names || []).filter(Boolean).join(", ")
    || (o.items || []).map((i) => i.vendor_business_name).filter(Boolean).join(", ")
    || "CityShor Exhibitor";
  const shortId = o.id.slice(0, 8).toUpperCase();
  const canTrack = !!(o.tracking_number || o.tracking_url);
  const shipmentHref = o.tracking_url || null;

  return (
    <div data-testid={`order-${o.id}`} className="p-4 bg-white border border-gray-200 rounded-2xl">
      <div className="flex gap-4">
        {firstItem?.image_base64 ? (
          <img
            data-testid={`order-image-${o.id}`}
            src={firstItem.image_base64}
            alt={firstItem?.name || "product"}
            className="h-20 w-20 rounded-xl object-cover flex-shrink-0"
          />
        ) : (
          <div className="h-20 w-20 rounded-xl bg-gray-100 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span data-testid={`order-id-${o.id}`} className="text-xs font-mono-tab text-gray-500">#{shortId}</span>
                <StatusPill status={o.status} testId={`order-status-pill-${o.id}`} />
              </div>
              <div className="mt-1 font-medium text-sm truncate">
                {firstItem?.name || "Order"}{extraCount > 0 && <span className="text-gray-500 font-normal"> + {extraCount} more</span>}
              </div>
              <div data-testid={`order-vendor-${o.id}`} className="text-xs text-gray-500 mt-0.5">Sold by <span className="text-gray-700">{soldBy}</span></div>
              <div data-testid={`order-date-${o.id}`} className="text-xs text-gray-500">Placed on {inrDate(o.created_at)}</div>
            </div>
            <div className="text-right">
              <div data-testid={`order-total-${o.id}`} className="font-display text-lg">₹{Number(o.total || 0).toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-gray-500">{o.payment_method === "cod" ? "Cash on Delivery" : o.payment_method === "bank_transfer" ? "Bank Transfer" : "Paid Online"}</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {canTrack && (
              shipmentHref ? (
                <a
                  data-testid={`order-track-link-${o.id}`}
                  href={shipmentHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F48E2E] text-white text-xs font-medium hover:bg-[#D97815] transition-colors"
                >
                  <Truck className="h-3.5 w-3.5" /> Track Shipment <ExternalLink className="h-3 w-3 opacity-80" />
                </a>
              ) : (
                <button
                  data-testid={`order-track-link-${o.id}`}
                  onClick={() => setExpanded(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F48E2E] text-white text-xs font-medium hover:bg-[#D97815] transition-colors"
                >
                  <Truck className="h-3.5 w-3.5" /> Track Shipment
                </button>
              )
            )}
            {o.tracking_number && (
              <span data-testid={`order-tracking-inline-${o.id}`} className="text-[11px] text-gray-500">
                {o.courier ? `${o.courier} · ` : ""}<span className="font-mono-tab text-gray-700">{o.tracking_number}</span>
              </span>
            )}
            <Link
              to={`/orders/${o.id}`}
              data-testid={`order-detail-${o.id}`}
              className="ml-auto text-xs text-[#F48E2E] hover:underline"
            >
              View details
            </Link>
            <button
              data-testid={`order-expand-${o.id}`}
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[#F48E2E]"
            >
              {expanded ? <>Hide timeline <ChevronUp className="h-3.5 w-3.5" /></> : <>Show timeline <ChevronDown className="h-3.5 w-3.5" /></>}
            </button>
          </div>
          {expanded && <BuyerOrderTracker order={o} testIdPrefix="my-orders-track" />}
        </div>
      </div>
    </div>
  );
}

export default function MyOrders() {
  const loc = useLocation();
  const { orders, notifs, loading } = useLiveOrders(loc.pathname);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter || (filter === "processing" && o.status === "confirmed"));
  const counts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const tabs = [
    { k: "all", label: `All (${orders.length})` },
    { k: "awaiting_vendor", label: `Awaiting vendor (${counts.awaiting_vendor || 0})` },
    { k: "processing", label: `Confirmed (${(counts.processing || 0) + (counts.confirmed || 0)})` },
    { k: "packed", label: `Packed (${counts.packed || 0})` },
    { k: "shipped", label: `Shipped (${counts.shipped || 0})` },
    { k: "delivered", label: `Delivered (${counts.delivered || 0})` },
    { k: "cancelled", label: `Cancelled (${counts.cancelled || 0})` },
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 lg:px-8 py-10" data-testid="my-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-1">Order Tracking</div>
          <h1 className="font-display text-4xl">My Orders</h1>
        </div>
        <span className="text-[11px] uppercase tracking-widest text-gray-500" data-testid="live-hint">Updates automatically</span>
      </div>

      {notifs.length > 0 && (
        <div className="mb-6 p-4 bg-[#FEF3E6] border border-[#F48E2E]/30 rounded-2xl" data-testid="buyer-notifications">
          <div className="text-xs uppercase tracking-widest text-[#F48E2E] font-bold mb-2">Recent updates</div>
          <div className="space-y-2">
            {notifs.slice(0, 5).map((n) => (
              <div key={n.id} className="text-sm text-gray-800">
                <span className="font-semibold">{n.title}.</span> {n.body}
                <span className="text-xs text-gray-500 ml-2">{inrDate(n.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="order-filters">
        {tabs.map((t) => (
          <button
            key={t.k}
            data-testid={`filter-${t.k}`}
            onClick={() => setFilter(t.k)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === t.k ? "bg-[#F48E2E] text-white border-[#F48E2E]" : "bg-white text-gray-600 border-gray-200 hover:border-[#F48E2E] hover:text-[#F48E2E]"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500" data-testid="orders-empty">
          {orders.length === 0 ? "No orders yet. Your first order will show up here." : `No orders in this stage right now.`}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => <OrderCard key={o.id} order={o} />)}
        </div>
      )}
    </div>
  );
}
